/**
 * The page header inside the 1240px container (design-system.md §3, §6).
 *
 * A flex row aligned to the top: identity on the left — title, optional
 * subtitle — and the screen's actions on the right. `align-items: flex-start`
 * matters, because a two-line title must not drag the action pills down with it.
 */
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { HeaderAction } from './header-action';
import { HeaderActionButton } from './header-action';
import { HeaderOverflowMenu } from './header-overflow-menu';

interface AdminPageHeaderProps {
  title: string;
  titleBadge?: ReactNode;
  subtitle?: string;
  /** Status pill, age chip, ID chip — the row that sits above the title. */
  meta?: ReactNode;
  /**
   * The row opposite the title, where a screen's actions sit — for a screen
   * whose header carries a *reading* rather than a button.
   *
   * `.cat-head-meta` is the handoff's one instance: the Catalogue's feed status
   * pill (CAE-1555), which belongs on the right because it is a fact about the
   * data the page is showing rather than about the page's subject. `meta`'s
   * row, above the title, is for what qualifies the title itself.
   */
  aside?: ReactNode;
  actions: readonly HeaderAction[];
  /**
   * Actions that belong to the screen but not in the design's header — folded
   * into a `⋯` menu after the visible ones. Empty or absent renders nothing,
   * not an empty menu.
   */
  overflowActions?: readonly HeaderAction[];
  /** Names that menu, e.g. "More contest actions". */
  overflowLabel?: string;
  /** The menu trigger's test id — per screen, since every page shares this header. */
  overflowTestId?: string;
}

export const AdminPageHeader = ({
  title,
  titleBadge,
  subtitle,
  meta,
  aside,
  actions,
  overflowActions = [],
  overflowLabel = 'More actions',
  overflowTestId,
}: AdminPageHeaderProps) => (
  <Box
    component="header"
    sx={(theme) => ({
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: `${theme.tokens.space[6]}px`,
      flexWrap: 'wrap',
    })}
  >
    <Box
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: `${theme.tokens.space[1]}px`,
        minWidth: 0,
      })}
    >
      {meta ? (
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            gap: `${theme.tokens.space[2]}px`,
            flexWrap: 'wrap',
          })}
        >
          {meta}
        </Box>
      ) : null}
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: `${theme.tokens.space[3]}px`,
          flexWrap: 'wrap',
        })}
      >
        {/*
          A blank title still reaches here, because a screen may hand the header
          a badge or actions and carry its own heading in the content. Rendering
          the h1 anyway would put an empty heading in the document outline.
        */}
        {title.trim() ? <Typography variant="h1">{title}</Typography> : null}
        {titleBadge}
      </Box>
      {subtitle ? (
        <Typography variant="bodySecondary" sx={(theme) => ({ color: theme.tokens.color.ink3 })}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>

    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: `${theme.tokens.space[2]}px`,
        flexWrap: 'wrap',
      })}
    >
      {aside}
      {actions.map((action, index) => (
        <HeaderActionButton
          key={`${action.label}-${action.to ?? 'action'}-${index}`}
          action={action}
        />
      ))}
      <HeaderOverflowMenu label={overflowLabel} actions={overflowActions} testId={overflowTestId} />
    </Box>
  </Box>
);
